import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { useEffectiveReducedMotion } from './useReducedMotion';

// Desktop hover preview — the enlarged rendition that pops beside a card
// when the mouse rests on it.
//
// HISTORY — an earlier version of this lived inside CardTile, was positioned
// at the CURSOR, and followed it via onMouseMove. That is what made it
// fragile, and it was deleted wholesale in d5c1771 rather than fixed. The
// cursor anchor is the root cause of every guard it needed:
//
//   * following the cursor meant an onMouseMove handler, which fires during
//     drags and scrolls, so it needed an `e.buttons !== 0` test;
//   * opening AT the cursor meant it opened ON TOP of whatever was under the
//     cursor — including the card being dragged;
//   * a moving anchor has no stable relationship to the page, so scrolling
//     left it stranded and needed a wheel handler.
//
// Anchoring to the tile's rect instead removes all of that. There is no
// mousemove handler at all: the panel opens beside the card, stays put, and
// is dismissed by the events that invalidate the anchor (below). What
// remains is one intent delay and one drag check.
const PREVIEW_WIDTH = 280;
const PREVIEW_HEIGHT = 373; // PREVIEW_WIDTH * 4/3 (aspect-[3/4])
/** Gap between the card's edge and the panel. */
const GAP = 12;
/** Keep the panel this far off every viewport edge. */
const MARGIN = 8;

// Under CardDetail's lightbox (70) so an inspect always covers a lingering
// preview, above the tavern rails (45), discover/scouting (50) and the
// triple flourish (60) so it is never clipped by the surface it came from.
const HOVER_Z = 65;

export interface HoverAnchor {
  /** Viewport rect of the tile the preview belongs to. */
  rect: { top: number; left: number; width: number; height: number };
}

/**
 * Placement beside the anchor: right of the card, flipping to the left when
 * there is no room, and clamped into the viewport as a last resort. Vertically
 * centred on the card and clamped to the viewport.
 *
 * Never overlaps the anchor unless the viewport is narrower than
 * card + panel + gaps, in which case being readable beats being tidy.
 */
function placeBeside(rect: HoverAnchor['rect'], vw: number, vh: number) {
  const rightEdge = rect.left + rect.width;
  let left = rightEdge + GAP;
  if (left + PREVIEW_WIDTH > vw - MARGIN) {
    const flipped = rect.left - GAP - PREVIEW_WIDTH;
    left = flipped >= MARGIN ? flipped : vw - MARGIN - PREVIEW_WIDTH;
  }
  left = Math.max(MARGIN, left);

  const centred = rect.top + rect.height / 2 - PREVIEW_HEIGHT / 2;
  const top = Math.max(
    MARGIN,
    Math.min(vh - PREVIEW_HEIGHT - MARGIN, centred),
  );
  return { left, top };
}

// Takes the rendition as children rather than a Card: CardTile is the only
// caller and rendering one from here would make the two modules a cycle.
export function CardHoverPreview({
  anchor,
  onDismiss,
  children,
}: {
  anchor: HoverAnchor;
  /** Called when something invalidates the anchor — see the effect below. */
  onDismiss: () => void;
  children: ReactNode;
}) {
  const reducedMotion = useEffectiveReducedMotion();

  // Dismiss on anything that moves the card out from under the anchor rect,
  // or that means the player has switched from reading to acting. Scroll is
  // captured so inner scrollers (the rails) are caught, not just window.
  useEffect(() => {
    const dismiss = () => onDismiss();
    window.addEventListener('pointerdown', dismiss, true);
    window.addEventListener('wheel', dismiss, { capture: true, passive: true });
    window.addEventListener('scroll', dismiss, true);
    window.addEventListener('resize', dismiss);
    window.addEventListener('blur', dismiss);
    return () => {
      window.removeEventListener('pointerdown', dismiss, true);
      window.removeEventListener('wheel', dismiss, true);
      window.removeEventListener('scroll', dismiss, true);
      window.removeEventListener('resize', dismiss);
      window.removeEventListener('blur', dismiss);
    };
  }, [onDismiss]);

  if (typeof document === 'undefined') return null;

  const { left, top } = placeBeside(
    anchor.rect,
    window.innerWidth,
    window.innerHeight,
  );

  return createPortal(
    <motion.div
      initial={reducedMotion ? { opacity: 0 } : { opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: reducedMotion ? 0 : 0.12, ease: 'easeOut' }}
      // Purely informational and never a hit target: pointer-events-none is
      // what guarantees the panel can't steal a click, block a drop zone, or
      // trigger a mouseleave on the very card that opened it.
      className="pointer-events-none fixed drop-shadow-[0_8px_24px_rgba(0,0,0,0.55)]"
      style={{ left, top, width: PREVIEW_WIDTH, zIndex: HOVER_Z }}
      aria-hidden
    >
      {children}
    </motion.div>,
    document.body,
  );
}
